/** Settings → Taxes: editable table of rates saved as a whole. Prop-less; uses useSite(). */
import React, { useEffect, useState } from 'react';
import { Button, Empty, Input, InputNumber, Space, Spin, Switch, Table, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { Hint, listOf, toastErr, useSite } from './common';
import { CountrySelect } from './ShopCommon';

const { Text } = Typography;

const Taxes = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [rows, setRows] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [tax, setTax] = useState(null);

    useEffect(() => { api.get('/shop-settings').then((s) => setTax(s.tax_settings || {})).catch(() => setTax({})); }, [api]);
    const setStripeTax = async (patch) => {
        try {
            const s = await api.put('/shop-settings', { tax_settings: { ...tax, ...patch } });
            setTax(s.tax_settings || {});
            message.success(t('Saved'));
        } catch (e) { toastErr(e, t('Save failed')); }
    };

    useEffect(() => {
        let live = true;
        api.get('/tax-rates').then((d) => live && setRows(listOf(d).map((r, i) => ({ ...r, _k: r.id || `n${i}` }))))
            .catch((e) => { if (live) { if (e.status !== 404) toastErr(e); setRows([]); } });
        return () => { live = false; };
    }, [api]);

    const upd = (k, patch) => { setRows((rs) => rs.map((r) => (r._k === k ? { ...r, ...patch } : r))); setDirty(true); };
    const add = () => { setRows((rs) => [...rs, { _k: `n${Date.now()}`, country: undefined, region: '', name: 'Tax', rate: 0, included: false, shipping_taxable: false }]); setDirty(true); };
    const save = async () => {
        if (rows.some((r) => !r.country)) { message.error(t('Choose a country for every rate')); return; }
        setSaving(true);
        try {
            const d = await api.put('/tax-rates', { items: rows.map(({ _k, id, ...r }) => ({ ...(id ? { id } : {}), ...r, region: (r.region || '').trim().toUpperCase(), included: !!r.included, shipping_taxable: !!r.shipping_taxable })) });
            if (listOf(d).length || Array.isArray(d)) setRows(listOf(d).map((r, i) => ({ ...r, _k: r.id || `n${i}` })));
            setDirty(false);
            message.success(t('Saved'));
        } catch (e) { toastErr(e, t('Save failed')); } finally { setSaving(false); }
    };

    if (!rows) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
    return (
        <div>
            <Space wrap style={{ marginBottom: 16 }}>
                <Switch checked={!!tax?.stripe_tax} disabled={!tax} onChange={(v) => setStripeTax({ stripe_tax: v })} />
                <Text>{t('Use Stripe Tax')}</Text>
                <Hint title={t('Stripe calculates tax at checkout. The rates below still apply to manual payments and gift card orders.')} />
                {tax?.stripe_tax && <><Switch size="small" checked={!!tax.prices_include_tax} onChange={(v) => setStripeTax({ prices_include_tax: v })} /><Text>{t('Included in prices')}</Text></>}
            </Space>
            <Table size="small" rowKey="_k" dataSource={rows} pagination={false} scroll={{ x: 880 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No taxes are charged')} /> }}
                columns={[
                    { title: t('Country'), key: 'c', width: 220, render: (_, r) => <CountrySelect value={r.country} onChange={(v) => upd(r._k, { country: v })} style={{ width: '100%' }} placeholder={t('Country')} /> },
                    { title: <span>{t('Region')}<Hint title={t('State/province code; empty = whole country')} /></span>, key: 'r', width: 110, render: (_, r) => <Input value={r.region} placeholder="CA" onChange={(e) => upd(r._k, { region: e.target.value })} /> },
                    { title: t('Name'), key: 'n', width: 140, render: (_, r) => <Input value={r.name} onChange={(e) => upd(r._k, { name: e.target.value })} /> },
                    { title: t('Rate', { context: 'tax' }), key: 'rate', width: 120, render: (_, r) => <InputNumber min={0} max={100} step={0.1} addonAfter="%" value={(r.rate || 0) / 100} onChange={(v) => upd(r._k, { rate: Math.round((v || 0) * 100) })} /> },
                    { title: <span>{t('Reduced rate')}<Hint title={t('For products in the reduced tax class; empty = same rate. Zero-class products are never taxed.')} /></span>, key: 'red', width: 120, render: (_, r) => <InputNumber min={0} max={100} step={0.1} addonAfter="%" value={r.class_rates?.reduced === undefined ? null : r.class_rates.reduced / 100} onChange={(v) => upd(r._k, { class_rates: v === null ? {} : { reduced: Math.round(v * 100) } })} /> },
                    { title: <span>{t('Included in prices')}</span>, key: 'i', width: 140, render: (_, r) => <Switch size="small" checked={!!r.included} onChange={(v) => upd(r._k, { included: v })} /> },
                    { title: t('Tax shipping'), key: 's', width: 110, render: (_, r) => <Switch size="small" checked={!!r.shipping_taxable} onChange={(v) => upd(r._k, { shipping_taxable: v })} /> },
                    { key: 'x', width: 50, render: (_, r) => <Button type="text" danger icon={<DeleteOutlined />} onClick={() => { setRows((rs) => rs.filter((x) => x._k !== r._k)); setDirty(true); }} /> },
                ]} />
            <Space style={{ marginTop: 12 }}>
                <Button icon={<PlusOutlined />} onClick={add}>{t('Add tax rate')}</Button>
                <Button type="primary" disabled={!dirty} loading={saving} onClick={save}>{t('Save')}</Button>
                {dirty && <Text type="secondary">{t('Unsaved changes')}</Text>}
            </Space>
        </div>
    );
};

export default Taxes;
